import { useState } from "react";

import { Card } from "primereact/card";
import { TabView, TabPanel } from "primereact/tabview";
import { InputSwitch } from "primereact/inputswitch";
import { Skeleton } from "primereact/skeleton";

import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import AspenAdaptCWEs from "@/components/aspen/AspenAdaptCWEs";
import AspenGuardianScans from "@/components/aspen/AspenGuardianScans";
import { useAspenFeatures } from "@/hooks/useAspenFeatures";

interface TabToggleOnlyMode {
  assist: boolean;
  guardian: boolean;
  adapt: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Aspen = () => {
  useBreadcrumb({
    label: "Aspen",
    route: AdminAppRoutes.routes.aspen.route,
    display: "none",
    resetTrail: true,
  });

  const {
    features,
    isLoading,
    viewOnly,
    currentTenantName,
    allDisabled,
    toggleFeature,
    toggleAll,
  } = useAspenFeatures();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [tabToggleOnlyMode, setTabToggleOnlyMode] = useState<TabToggleOnlyMode>({
    assist: false,
    guardian: false,
    adapt: false,
  });

  const handleTabChange = (e: { index: number }) => {
    setActiveTabIndex(e.index);
    setTabToggleOnlyMode({ assist: false, guardian: false, adapt: false });
  };

  if (isLoading)
    return (
      <>
        <div className="mb-4">
          <h1>Aspen</h1>
        </div>
        <div>
          <Skeleton width="100%" height="450px" />
        </div>
      </>
    );

  return (
    <>
      <div className="mb-4">
        <h1>Aspen</h1>
      </div>

      <TabView activeIndex={activeTabIndex} onTabChange={handleTabChange}>
        {/* ---------------------------------------------------------------- */}
        {/* Settings tab                                                      */}
        {/* ---------------------------------------------------------------- */}
        <TabPanel header="Settings">
          <Card pt={{ content: { className: "p-0" } }} className="mb-4">
            <p>
              Security Journey takes the security of your data seriously. This page lets you manage
              all of Security Journey&apos;s AI functionality in one place, so you can be sure your
              data is being handled according to your company policies. You can disable our Aspen AI
              product entirely or toggle individual features on or off to control which capabilities
              are available to your team.
            </p>
            <div className="mt-4 flex items-center">
              <strong className="flex-1">Disable all Aspen features</strong>
              <InputSwitch
                checked={allDisabled}
                onChange={e => void toggleAll(e.value)}
                disabled={viewOnly}
              />
            </div>
          </Card>

          <Card pt={{ content: { className: "p-0" } }} className="mb-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <strong>Aspen Assist</strong>
                  <p className="body-sm text-secondary">AI-powered hints in sandbox lessons</p>
                </div>
                <InputSwitch
                  checked={features.assist}
                  onChange={e => void toggleFeature("assist", e.value)}
                  disabled={viewOnly}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <strong>Aspen Guardian</strong>
                  <p className="body-sm text-secondary">
                    Automated AI instruction file updates from vulnerability scans
                  </p>
                </div>
                <InputSwitch
                  checked={features.guardian}
                  onChange={e => void toggleFeature("guardian", e.value)}
                  disabled={viewOnly}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <strong>Aspen Adapt</strong>
                  <p className="body-sm text-secondary">
                    CWE-based training recommendations from developer activity
                  </p>
                </div>
                <InputSwitch
                  checked={features.adapt}
                  onChange={e => void toggleFeature("adapt", e.value)}
                  disabled={viewOnly}
                />
              </div>
            </div>
          </Card>
        </TabPanel>

        {/* ---------------------------------------------------------------- */}
        {/* Aspen Assist tab                                                  */}
        {/* ---------------------------------------------------------------- */}
        {(features.assist || tabToggleOnlyMode.assist) && (
          <TabPanel header="Aspen Assist">
            <Card pt={{ content: { className: "p-0" } }} className="mb-4">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-surface-200">
                <strong>Aspen Assist</strong>
                <InputSwitch
                  checked={features.assist}
                  onChange={e => {
                    void toggleFeature("assist", e.value);
                    if (!e.value) setTabToggleOnlyMode(prev => ({ ...prev, assist: true }));
                  }}
                  disabled={viewOnly}
                />
              </div>
              <p>
                Aspen Assist is available in most of our sandbox lessons, and provides hints to
                users as they attempt to remediate the vulnerability found in the sandbox.
              </p>
              <p className="mt-2">
                To generate a relevant hint, we send the following information to our AI provider:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2">
                <li>The reference solution that we have developed to fix the vulnerability</li>
                <li>The last 60 minutes of the user&apos;s code submissions for this lesson</li>
                <li>
                  A prompt that tells the LLM to generate a hint for the user, without directly
                  revealing the answer
                </li>
              </ul>
              <p className="mt-2">
                This data is provided in a context that does not remember or train the model on the
                submission in any way.
              </p>
            </Card>
          </TabPanel>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Aspen Guardian tab                                                */}
        {/* ---------------------------------------------------------------- */}
        {(features.guardian || tabToggleOnlyMode.guardian) && (
          <TabPanel header="Aspen Guardian">
            <Card pt={{ content: { className: "p-0" } }} className="mb-4">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-surface-200">
                <strong>Aspen Guardian</strong>
                <InputSwitch
                  checked={features.guardian}
                  onChange={e => {
                    void toggleFeature("guardian", e.value);
                    if (!e.value) setTabToggleOnlyMode(prev => ({ ...prev, guardian: true }));
                  }}
                  disabled={viewOnly}
                />
              </div>
              <p>
                Aspen Guardian can automatically update your AI instruction files based on
                vulnerability scan results, helping AI assistants learn from security issues to
                avoid them in future code generation.
              </p>
              <a
                href="https://github.com/SecurityJourney/aspen-connector"
                className={`block mt-4 ${themeClasses.textPrimaryDarker}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Connect your GitHub workflows with Aspen Guardian
              </a>
            </Card>

            <Card pt={{ content: { className: "p-0" } }} className="mb-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <strong>Record Instruction Files</strong>
                    <p className="body-sm text-secondary">
                      Enable reviewing updates to instructions after Aspen Guardian has processed
                      them
                    </p>
                  </div>
                  <InputSwitch
                    checked={features.recordInstructionFiles}
                    onChange={e => void toggleFeature("recordInstructionFiles", e.value)}
                    disabled={viewOnly}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <strong>Record CWEs with Aspen Adapt</strong>
                    <p className="body-sm text-secondary">
                      Forward CWE findings to Aspen Adapt to assign relevant content to your
                      developers
                    </p>
                  </div>
                  <InputSwitch
                    checked={features.forwardCwes}
                    onChange={e => void toggleFeature("forwardCwes", e.value)}
                    disabled={viewOnly}
                  />
                </div>
              </div>
            </Card>

            <AspenGuardianScans showInstructions={features.recordInstructionFiles} />
          </TabPanel>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Aspen Adapt tab                                                   */}
        {/* ---------------------------------------------------------------- */}
        {(features.adapt || tabToggleOnlyMode.adapt) && (
          <TabPanel header="Aspen Adapt">
            <Card pt={{ content: { className: "p-0" } }} className="mb-4">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-surface-200">
                <strong>Aspen Adapt</strong>
                <InputSwitch
                  checked={features.adapt}
                  onChange={e => {
                    void toggleFeature("adapt", e.value);
                    if (!e.value) setTabToggleOnlyMode(prev => ({ ...prev, adapt: true }));
                  }}
                  disabled={viewOnly}
                />
              </div>
              <p>
                Aspen Adapt turns real code activity into targeted, secure coding training by
                assigning hyper-relevant content to your developers based on the CWEs they commit.
                Learn how to connect Aspen directly to your source code management tool or send CWE
                findings to Security Journey via API{" "}
                <a
                  href="https://help.securityjourney.com/aspen-ai#adapt"
                  className={themeClasses.linkPrimary}
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </p>
              <a
                href="https://github.com/SecurityJourney/aspen-connector"
                className={`block mt-4 ${themeClasses.textPrimaryDarker}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Connect your GitHub workflows to Aspen Adapt
              </a>
            </Card>

            {features.adapt && (
              <Card pt={{ content: { className: "p-0" } }} className="mb-4">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <strong>Record Repository Name</strong>
                      <p className="body-sm text-secondary">
                        Identify what git repository a vulnerability was committed in
                      </p>
                    </div>
                    <InputSwitch
                      checked={features.recordRepoName}
                      onChange={e => void toggleFeature("recordRepoName", e.value)}
                      disabled={viewOnly}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <strong>Record Username</strong>
                      <p className="body-sm text-secondary">
                        Identify user by GitHub username, which may differ from email
                      </p>
                    </div>
                    <InputSwitch
                      checked={features.recordUsername}
                      onChange={e => void toggleFeature("recordUsername", e.value)}
                      disabled={viewOnly}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <strong>Record PR Number</strong>
                      <p className="body-sm text-secondary">
                        Identify what pull request a vulnerability was committed in
                      </p>
                    </div>
                    <InputSwitch
                      checked={features.recordPrNumber}
                      onChange={e => void toggleFeature("recordPrNumber", e.value)}
                      disabled={viewOnly}
                    />
                  </div>
                </div>
              </Card>
            )}

            {features.adapt && (
              <AspenAdaptCWEs
                tenant={currentTenantName}
                showRepository={features.recordRepoName}
                showUsername={features.recordUsername}
                showPrNumber={features.recordPrNumber}
              />
            )}
          </TabPanel>
        )}
      </TabView>
    </>
  );
};

export default Aspen;
